import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, Calendar, User, Edit2 } from "iconsax-react";
import type { Icon } from "iconsax-react";
import { ModalForm, ModalWrapper, Button, Prompt, Skeleton, FileViewer, PromptForm, Badge, Select } from "@/shared/ui";
import { extractErrorMessage, formatDateForDisplay, downloadBlob, ensurePdfExtension } from "@/shared/utils";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { useOrderSigning, RevokeSigningButton } from "@/features/signing";
import { ReviewNoteModal, DetailFieldRow, DetailModalFooterActions } from "@/features/hr/shared";
import {
  useGetResignationLetterQuery,
  useDeleteResignationLetterMutation,
  useUpdateResignationLetterMutation,
  useCancelResignationLetterMutation,
  usePreviewResignationApplicationMutation,
  usePreviewTerminationOrderMutation,
  useSignTerminationOrderMutation,
  useApproveResignationMutation,
  useRejectResignationMutation,
  useRequestRevisionResignationMutation,
  useRevokeResignationMutation,
} from "../api";
import type { ResignationStatus, ApprovalResolution } from "../types";
import { getAvailableActions, filterActionsByPermissions, type ResignationAction } from "../utils/resignationRules";
import { RESIGNATION_STATUS_MAP } from "../consts";

interface Props {
  resignationId: number;
  onClose: () => void;
  onSignSuccess?: () => void;
}

interface ResignationField {
  key: string;
  label: string;
  value: string;
  icon: Icon;
  isStatus?: boolean;
  statusComponent?: ReactNode;
}

export default function ResignationLetterDetailsModal({ resignationId, onClose, onSignSuccess }: Props) {
  const { t } = useTranslation("ContractsPage");
  const { hasPermission, hasRole } = useAuth();
  const { data: resignationData, isLoading, isError } = useGetResignationLetterQuery(resignationId);
  const [deleteResignation, { isLoading: isDeleting }] = useDeleteResignationLetterMutation();
  const [updateResignation, { isLoading: isUpdating }] = useUpdateResignationLetterMutation();
  const [cancelResignation, { isLoading: isCancelling }] = useCancelResignationLetterMutation();
  const [previewApplication] = usePreviewResignationApplicationMutation();
  const [previewOrder, { isLoading: isDownloadingOrder }] = usePreviewTerminationOrderMutation();
  const [signOrder] = useSignTerminationOrderMutation();
  const [approveResignation] = useApproveResignationMutation();
  const [rejectResignation, { isLoading: isRejecting }] = useRejectResignationMutation();
  const [revisionResignation, { isLoading: isRevisioning }] = useRequestRevisionResignationMutation();
  const [revokeResignation] = useRevokeResignationMutation();

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [showRejectNoteModal, setShowRejectNoteModal] = useState(false);
  const [showRevisionNoteModal, setShowRevisionNoteModal] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedResolution, setEditedResolution] = useState<ApprovalResolution | null>(null);
  const [previewingOrderId, setPreviewingOrderId] = useState<number | null>(null);

  const { t: tCommon } = useTranslation("Common");

  const approveSigning = useOrderSigning({
    previewFn() {
      return previewApplication(resignationId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature, fileBase64) {
      return approveResignation({ id: resignationId, cms_signature: cmsSignature, file_base64: fileBase64! }).unwrap();
    },
    onSuccess() {
      setPrompt({
        title: t("resignationLetter.actions.approveSuccessTitle"),
        text: t("resignationLetter.actions.approveSuccessText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const orderSigning = useOrderSigning({
    previewFn() {
      return previewOrder(resignationId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      const orderId = resignationData?.order?.id;
      if (!orderId) throw new Error("No order ID");
      return signOrder({ id: orderId, resignationId, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: tCommon("signing.success.title"),
        text: tCommon("signing.success.text"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  async function handleRejectConfirm(note: string) {
    if (isRejecting) return;
    try {
      await rejectResignation({ id: resignationId, note: note.trim() || undefined }).unwrap();
      setShowRejectNoteModal(false);
      setPrompt({
        title: t("resignationLetter.actions.rejectSuccessTitle"),
        text: t("resignationLetter.actions.rejectSuccessText"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("resignationLetter.actions.errorTitle"),
        text: extractErrorMessage(error, "resignationLetter.actions.rejectError", t),
        variant: "error",
      });
    }
  }

  async function handleRevisionConfirm(note: string) {
    if (isRevisioning) return;
    try {
      await revisionResignation({ id: resignationId, note: note.trim() }).unwrap();
      setShowRevisionNoteModal(false);
      setPrompt({
        title: t("resignationLetter.actions.revisionSuccessTitle"),
        text: t("resignationLetter.actions.revisionSuccessText"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("resignationLetter.actions.errorTitle"),
        text: extractErrorMessage(error, "resignationLetter.actions.revisionError", t),
        variant: "error",
      });
    }
  }

  async function handleDelete() {
    if (isDeleting) return;
    try {
      await deleteResignation(resignationId).unwrap();
      onClose();
    } catch (error) {
      setPrompt({
        title: t("resignationLetter.actions.errorTitle"),
        text: extractErrorMessage(error, "resignationLetter.actions.deleteError", t),
        variant: "error",
      });
    }
  }

  async function handleCancel() {
    if (isCancelling) return;
    try {
      await cancelResignation(resignationId).unwrap();
      setShowCancelPrompt(false);
      setPrompt({
        title: t("resignationLetter.actions.cancelSuccessTitle"),
        text: t("resignationLetter.actions.cancelSuccessText"),
        variant: "success",
      });
    } catch (error) {
      setShowCancelPrompt(false);
      setPrompt({
        title: t("resignationLetter.actions.errorTitle"),
        text: extractErrorMessage(error, "resignationLetter.actions.cancelError", t),
        variant: "error",
      });
    }
  }

  function handleRevoke() {
    return revokeResignation(resignationId).unwrap();
  }

  async function handleDownloadOrderDocx() {
    if (previewingOrderId !== null) return;
    try {
      setPreviewingOrderId(resignationId);
      const { blob, filename } = await previewOrder(resignationId).unwrap();
      downloadBlob(blob, ensurePdfExtension(filename || t("downloads.resignationOrderFileName", { resignationId })));
    } catch (error) {
      setPrompt({
        title: t("resignationLetter.actions.errorTitle"),
        text: extractErrorMessage(error, "resignationLetter.actions.downloadOrderDocxError", t),
        variant: "error",
      });
    } finally {
      setPreviewingOrderId(null);
    }
  }

  function handleOpenEditModal() {
    setEditedResolution((resignationData?.approval_resolution as ApprovalResolution) || null);
    setShowEditModal(true);
  }

  async function handleSaveResolution() {
    if (isUpdating) return;
    try {
      await updateResignation({
        id: resignationId,
        data: { approval_resolution: editedResolution || undefined },
      }).unwrap();
      setShowEditModal(false);
      setPrompt({
        title: t("resignationLetter.actions.successTitle"),
        text: t("resignationLetter.actions.updateSuccess"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("resignationLetter.actions.errorTitle"),
        text: extractErrorMessage(error, "resignationLetter.actions.updateError", t),
        variant: "error",
      });
    }
  }

  function handleAction(action: ResignationAction) {
    switch (action) {
      case "approve":
        approveSigning.handleSign();
        break;
      case "reject":
        setShowRejectNoteModal(true);
        break;
      case "request_revision":
        setShowRevisionNoteModal(true);
        break;
      case "revoke":
        break;
      case "sign_order":
        orderSigning.handleSign();
        break;
      case "download_order_docx":
        handleDownloadOrderDocx();
        break;
      case "edit":
        handleOpenEditModal();
        break;
      case "cancel":
        setShowCancelPrompt(true);
        break;
      case "delete":
        setShowDeletePrompt(true);
        break;
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSignSuccess?.();
    }
  }

  function filterField(field: ResignationField): boolean {
    return !!(field.value || field.isStatus);
  }

  function renderField(field: ResignationField, index: number, array: ResignationField[]) {
    return (
      <DetailFieldRow
        key={field.key}
        label={field.label}
        value={field.value}
        icon={field.icon}
        isStatus={field.isStatus}
        statusComponent={field.statusComponent}
        isLast={index === array.length - 1}
      />
    );
  }

  function renderResignationContent(resignation: NonNullable<typeof resignationData>) {
    const status = resignation.status;

    function getStatusBadge(resignationStatus: ResignationStatus) {
      if (resignationStatus === "draft" && resignation.application_review_note) {
        return (
          <Badge
            variant="soft"
            color="notice"
            text={t("resignationLetter.statusLabels.revision_requested")}
            icon={<Edit2 size={14} color="currentColor" />}
          />
        );
      }
      const config = RESIGNATION_STATUS_MAP[resignationStatus];
      return <Badge variant="soft" color={config.color} text={t(config.label)} icon={config.icon} />;
    }

    const fields: ResignationField[] = [
      {
        key: "status",
        label: t("resignationLetter.status"),
        value: "",
        icon: DocumentText1,
        isStatus: true,
        statusComponent: getStatusBadge(status),
      },
      {
        key: "lastWorkingDay",
        label: t("resignationLetter.lastWorkingDay"),
        value: resignation.last_working_day ? formatDateForDisplay(resignation.last_working_day, false) : "",
        icon: Calendar,
      },
      {
        key: "approvalResolution",
        label: t("resignationLetter.approvalResolution"),
        value: resignation.approval_resolution_display || "",
        icon: DocumentText1,
      },
      {
        key: "worker",
        label: t("resignationLetter.worker"),
        value: resignation.worker?.full_name || "",
        icon: User,
      },
      {
        key: "createdAt",
        label: t("resignationLetter.createdAt"),
        value: resignation.created_at ? formatDateForDisplay(resignation.created_at, true) : "",
        icon: Calendar,
      },
    ];

    if (resignation.application_review_note) {
      fields.push({
        key: "revisionNote",
        label: t("resignationLetter.revisionNote"),
        value: resignation.application_review_note,
        icon: DocumentText1,
      });
    }

    const filteredFields = fields.filter(filterField);
    const showApplicationDoc =
      resignation.application_document &&
      (resignation.application_document.signed_url || resignation.application_document.original_url);
    const showOrderDoc =
      resignation.order_document &&
      (resignation.order_document.signed_url || resignation.order_document.original_url);

    return (
      <>
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
          <h4 className="text-display-2xs content-base-primary">{t("resignationLetter.title")}</h4>
        </div>

        <div className="flex-1 min-h-0 p-1">
          <div className="flex flex-col py-4">
            <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
              <p className="text-body-bold-lg content-base-primary">
                {t("resignationLetter.prefix")} №{resignation.id}
              </p>
            </div>

            <div className="flex flex-col">{filteredFields.map(renderField)}</div>

            {showApplicationDoc && (
              <div className="mt-4 border-t surface-base-stroke pt-4">
                <p className="text-body-bold-md content-base-primary mb-2">{t("resignationLetter.applicationPdf")}</p>
                <FileViewer
                  existingFileUrl={
                    (resignation.application_document.signed_url || resignation.application_document.original_url) as string
                  }
                  secondaryFileUrl={
                    resignation.application_document.signed_url && resignation.application_document.original_url
                      ? resignation.application_document.original_url
                      : undefined
                  }
                />
              </div>
            )}

            {showOrderDoc && (
              <div className="mt-4 border-t surface-base-stroke pt-4">
                <p className="text-body-bold-md content-base-primary mb-2">{t("resignationLetter.orderPdf")}</p>
                <FileViewer
                  existingFileUrl={
                    (resignation.order_document.signed_url || resignation.order_document.original_url) as string
                  }
                  secondaryFileUrl={
                    resignation.order_document.signed_url && resignation.order_document.original_url
                      ? resignation.order_document.original_url
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderFooter() {
    if (isLoading || !resignationData) return undefined;

    const { actions: allActions } = getAvailableActions(
      resignationData,
      {
        isDownloadingOrder,
        isCancelling,
        isDeleting,
        isRevisioning,
      },
      t,
    );

    const actions = filterActionsByPermissions(allActions, hasPermission, hasRole);

    if (actions.length === 0) return undefined;

    const buttonSize: "lg" | "huge" = actions.length >= 4 ? "lg" : "huge";

    function renderActionButton(action: (typeof actions)[number]) {
      if (action.id === "revoke") {
        return <RevokeSigningButton key="revoke" size={buttonSize} onRevoke={handleRevoke} onSuccess={onClose} />;
      }

      const isDisabled =
        (action.id === "approve" && approveSigning.isLoading) ||
        (action.id === "reject" && isRejecting) ||
        (action.id === "request_revision" && isRevisioning) ||
        (action.id === "sign_order" && orderSigning.isLoading) ||
        (action.id === "download_order_docx" && isDownloadingOrder) ||
        (action.id === "edit" && isUpdating) ||
        (action.id === "cancel" && isCancelling) ||
        (action.id === "delete" && isDeleting);

      return (
        <Button
          key={action.id}
          variant={action.variant}
          size={buttonSize}
          className="w-full"
          disabled={isDisabled}
          onClick={() => handleAction(action.id)}>
          {action.icon}
          {action.label}
        </Button>
      );
    }

    return (
      <DetailModalFooterActions
        actions={actions}
        renderButton={function renderButton(action) {
          return renderActionButton(action as (typeof actions)[number]);
        }}
      />
    );
  }

  return (
    <>
      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="ContractsPage"
        />
      )}

      {showDeletePrompt && (
        <PromptForm
          title={t("resignationLetter.actions.deleteConfirmTitle")}
          text={t("resignationLetter.actions.deleteConfirmText")}
          confirmText={t("buttons.delete")}
          cancelText={t("buttons.cancel")}
          onConfirm={handleDelete}
          onClose={() => setShowDeletePrompt(false)}
          isLoading={isDeleting}
          variant="error"
        />
      )}

      {showCancelPrompt && (
        <PromptForm
          title={t("resignationLetter.actions.cancelConfirmTitle")}
          text={t("resignationLetter.actions.cancelConfirmText")}
          confirmText={t("buttons.confirm")}
          cancelText={t("buttons.cancel")}
          onConfirm={handleCancel}
          onClose={() => setShowCancelPrompt(false)}
          isLoading={isCancelling}
          variant="error"
        />
      )}

      {approveSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${approveSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {orderSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${orderSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {showRejectNoteModal && (
        <ReviewNoteModal
          action="reject"
          onClose={() => setShowRejectNoteModal(false)}
          onSubmit={handleRejectConfirm}
          isSubmitting={isRejecting}
          namespace="ContractsPage"
          translationPrefix="resignationLetter.review"
        />
      )}

      {showRevisionNoteModal && (
        <ReviewNoteModal
          action="revision"
          onClose={() => setShowRevisionNoteModal(false)}
          onSubmit={handleRevisionConfirm}
          isSubmitting={isRevisioning}
          namespace="ContractsPage"
          translationPrefix="resignationLetter.review"
        />
      )}

      {previewingOrderId !== null && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("resignationLetter.messages.loader.downloadingOrder")}
          additionalText={t("resignationLetter.messages.loader.pleaseWait")}
          namespace="ContractsPage"
        />
      )}

      {showEditModal && (
        <ModalWrapper onClose={() => setShowEditModal(false)}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 border-b surface-base-stroke pb-3">
              <h4 className="text-display-2xs content-base-primary">{t("resignationLetter.actions.editTitle")}</h4>
            </div>

            <Select
              label={t("resignationLetter.approvalResolution")}
              placeholder={t("resignationLetter.selectResolution")}
              options={[
                {
                  label: t("resignationForm.approval_resolution.approved_with_1month"),
                  value: "approved_with_1month",
                },
                { label: t("resignationForm.approval_resolution.approved"), value: "approved" },
                {
                  label: t("resignationForm.approval_resolution.no_objection"),
                  value: "no_objection",
                },
              ]}
              value={editedResolution}
              onChange={(v) => setEditedResolution(v as ApprovalResolution)}
            />

            {isUpdating ? (
              <div className="grid grid-cols-[2fr_3fr] gap-3">
                <Skeleton height={44} width="100%" />
                <Skeleton height={44} width="100%" />
              </div>
            ) : (
              <div className="grid grid-cols-[2fr_3fr] gap-3">
                <Button variant="secondary" size="huge" onClick={() => setShowEditModal(false)}>
                  {t("buttons.cancel")}
                </Button>
                <Button variant="primary" size="huge" onClick={handleSaveResolution}>
                  {t("buttons.save")}
                </Button>
              </div>
            )}
          </div>
        </ModalWrapper>
      )}

      <ModalForm icon={DocumentText1} onClose={onClose} resize={false}footer={renderFooter()}>
        <div className="flex flex-col gap-6 h-full min-h-0">
          {isLoading ? (
            <>
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <Skeleton height={28} width={240} />
              </div>
              <div className="flex-1 min-h-0">
                <div className="flex flex-col py-4">
                  <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
                    <Skeleton height={24} width={200} />
                    <Skeleton height={32} width={150} />
                  </div>
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <div key={idx} className="py-3 border-b surface-base-stroke">
                        <Skeleton height={20} width="100%" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Skeleton height={44} width="100%" />
            </>
          ) : isError || !resignationData ? (
            <>
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <h4 className="text-display-2xs content-base-primary">{t("resignationLetter.title")}</h4>
              </div>
              <div className="flex-1 min-h-0 flex items-center justify-center">
                <p className="text-body-regular-md content-action-negative">{t("resignationLetter.loadError")}</p>
              </div>
            </>
          ) : (
            renderResignationContent(resignationData)
          )}
        </div>
      </ModalForm>
    </>
  );
}

